import type { MenuOption } from "../CoreInterstate";
import { HeartSolidIcon } from "../Icons";
import type { ResolvedAccountName } from "./accountOrdering";
import {
  compareAccountsByName,
  getAccountName,
  mapAccountNamesById,
  toAccountKey
} from "./accountOrdering";

/**
 * Builds the navbar account menu in display order: favorite accounts pinned to
 * the top alphabetically by account name, followed by the account history in
 * most-recently-used order (US2054268), with a divider between the two groups.
 * Every favorite carries a heart icon, so the group above the divider is
 * self-describing rather than relying on position alone.
 *
 * Ordering is driven by the requested id lists rather than by the order the
 * account-name service happens to return, because `useGetAccountNames` sorts
 * the ids it requests so that callers asking for the same accounts share one
 * cache entry. Accounts whose name has not resolved yet fall back to their id.
 *
 * Shared by DDC and Autotrader so the ordering rules the story defines live in
 * one place.
 */
export function buildAccountMenuOptions<TAccountId extends string | number>(
  favoriteAccountIds: readonly TAccountId[],
  recentAccountIds: readonly TAccountId[],
  resolvedAccountNames?: readonly ResolvedAccountName[]
): MenuOption[] {
  const accountNamesById = mapAccountNamesById(resolvedAccountNames);
  const toMenuOption = (accountId: TAccountId) => ({
    label: getAccountName(accountNamesById, accountId) || accountId.toString(),
    value: accountId.toString()
  });
  // An account that is both a favorite and recently visited is listed once, as
  // a favorite: favorites sort above the history, so that entry is already the
  // easier of the two to reach, and a repeated entry reads as the same option
  // twice to a screen reader.
  const favoriteKeys = new Set(favoriteAccountIds.map(toAccountKey));
  const recentOptions = recentAccountIds
    .filter(accountId => !favoriteKeys.has(toAccountKey(accountId)))
    .map(toMenuOption);
  const favoriteOptions = [...favoriteAccountIds]
    .sort(compareAccountsByName(accountNamesById))
    .map(accountId => ({
      ...toMenuOption(accountId),
      icon: (
        // Interstate icons hard-code `aria-hidden`, which would leave the
        // grouping visual-only; `aria-hidden={false}` plus the title id it
        // points `aria-labelledby` at makes each item announce as
        // "Favorite <account name>".
        <HeartSolidIcon
          aria-hidden={false}
          role="img"
          title="Favorite"
          titleId={`favorite-account-${accountId}`}
        />
      )
    }));
  // Separate the two groups with a rule under the last favorite, but only when
  // both groups have entries, so the menu never ends on a dangling divider.
  const dividerIndex = recentOptions.length ? favoriteOptions.length - 1 : -1;
  return [
    ...favoriteOptions.map((option, index) =>
      index === dividerIndex ? { ...option, divider: true } : option
    ),
    ...recentOptions
  ];
}
